import { addDays, DateOnly, differenceInDays, endOfDay, subDays } from '@if/core/date'
import { EPaymentFrequency } from '@if/core/types'
import { getToday } from '@src/services/DateTimeProvider'
import { SafePaymentActionDateService } from '@src/services/SafePaymentActionDateService'
import { EOperationStatus, EPaymentMethod } from '@src/types/constants'
import { OperationInformation } from '@src/types/loan/loan-operation'
import { CalendarSimulation } from '@src/types/loan/loan-simulation'

export const getLoanCalendarMaxFirstPaymentDifferenceInDays = (
  fromDate: Date,
  frequency: EPaymentFrequency,
): number => {
  return differenceInDays(addDays(fromDate, frequency === EPaymentFrequency.BiWeekly ? 14 : 30), fromDate)
}

const compatibleOperationStatus = new Set([
  EOperationStatus.Planned,
  EOperationStatus.Complete,
  EOperationStatus.Failed,
])

export const getLoanCalendarMaxPaymentDate = (
  simulation: CalendarSimulation,
  frequency: EPaymentFrequency,
  date: DateOnly,
): Date => {
  // Get the ppaPayments and sort them by their date.
  const ppaPayments = [...simulation.customerPaymentOperations]
    .filter(
      (x) =>
        compatibleOperationStatus.has(x.operation.operationStatusId) &&
        x.operation.paymentMethodId === EPaymentMethod.PreAuthorizedDebit,
    )
    .sort((a, b) => a.operation.effectiveDate.getTime() - b.operation.effectiveDate.getTime())

  // Take the last PPA payment that has been paid. If none has been paid yet, anchor on the loan's
  // activation date
  const anchorDate =
    ppaPayments.findLast((x) => x.operation.effectiveDate <= date)?.operation.effectiveDate ??
    simulation.fullLoan.loan.activationOn

  return endOfDay(addDays(anchorDate, getLoanCalendarMaxFirstPaymentDifferenceInDays(anchorDate, frequency)))
}

export const getLoanCalendarValidIntervalForNewFirstPaymentDateForOperation = (
  operationInformations: OperationInformation,
  frequency: EPaymentFrequency,
  daysLate: number,
  loanActivationDate: DateOnly,
  canBypassAcpLock: boolean,
): [Date, Date] => {
  const date = operationInformations.operation.effectiveDate
  const min = SafePaymentActionDateService.getInstance().getMinPaymentDate(
    EPaymentMethod.PreAuthorizedDebit,
    loanActivationDate,
    canBypassAcpLock,
  )
  const maxFirstPaymentDifference =
    daysLate > 0
      ? 14 // Number of max days when client is late
      : getLoanCalendarMaxFirstPaymentDifferenceInDays(date, frequency)
  const max = endOfDay(addDays(date, maxFirstPaymentDifference))
  return [min, max]
}

export const getAllLoanCalendarValidIntervalForNewFirstPaymentDateForOperation = (
  simulation: CalendarSimulation,
  frequency: EPaymentFrequency,
  canBypassAcpLock: boolean,
): [Date, Date][] => {
  const loanActivationDate = simulation.fullLoan.loan.activationOn
  const baselineDate = simulation.getNextValidActionDate(EPaymentMethod.PreAuthorizedDebit, canBypassAcpLock)
  const maxDayDiff = getLoanCalendarMaxFirstPaymentDifferenceInDays(baselineDate, frequency)
  // We only consider operations that are within the max day difference from today
  const minimumOperationEffectiveDate = new DateOnly(subDays(baselineDate, maxDayDiff))
  const daysLate = simulation.summaryInformation?.daysLate || 0
  return simulation.customerPaymentOperations.reduce<[Date, Date][]>((acc, opInfo) => {
    if (
      compatibleOperationStatus.has(opInfo.operation.operationStatusId) &&
      opInfo.operation.effectiveDate >= minimumOperationEffectiveDate
    ) {
      const interval = getLoanCalendarValidIntervalForNewFirstPaymentDateForOperation(
        opInfo,
        frequency,
        daysLate,
        loanActivationDate,
        canBypassAcpLock,
      )
      acc.push(interval)
    }
    return acc
  }, [])
}

export const isNewFirstPaymentValidForIntervals = (
  simulation: CalendarSimulation,
  frequency: EPaymentFrequency,
  date: Date,
  canBypassAcpLock: boolean,
): boolean => {
  const loanActivationDate = simulation.fullLoan.loan.activationOn
  const minPaymentDate = SafePaymentActionDateService.getInstance().getMinPaymentDate(
    EPaymentMethod.PreAuthorizedDebit,
    loanActivationDate,
    canBypassAcpLock,
  )
  if (date < minPaymentDate) {
    return false
  }
  const now = getToday()
  const maxDayDiff = getLoanCalendarMaxFirstPaymentDifferenceInDays(now, frequency)
  const minimumOperationEffectiveDate = subDays(now, maxDayDiff)
  const daysLate = simulation.summaryInformation?.daysLate || 0
  return simulation.customerPaymentOperations.some((opInfo) => {
    // skipping operations that are not CustomerPaymentOperation or have an incompatible status
    // or are before the minimum effective date
    if (
      !compatibleOperationStatus.has(opInfo.operation.operationStatusId) ||
      opInfo.operation.effectiveDate < minimumOperationEffectiveDate
    ) {
      return false
    }

    const [min, max] = getLoanCalendarValidIntervalForNewFirstPaymentDateForOperation(
      opInfo,
      frequency,
      daysLate,
      loanActivationDate,
      canBypassAcpLock,
    )
    return min <= date && date <= max
  })
}
